import React, { useState, useRef, useEffect } from 'react';
import { Shield, User, LogOut, Settings, BarChart2, Menu, X, ChevronDown, Crown, Zap, ClipboardList } from 'lucide-react';
import { s } from '../styles/shared';
import MarketPulzeLogo from './MarketPulzeLogo';

const Header = ({ user, page, setPage, setAnalysis, setShowAuth, setAuthMode, setShowInvite, isAdmin, isSuperAdmin, hasReviewConsoleAccess }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  // Close user menu on outside click
  useEffect(() => {
    const handler = (e) => { if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenuOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // FIXED (July 2026) — this used to compute isAdmin locally from
  // process.env.REACT_APP_ADMIN_EMAILS, a CRA build-time variable baked
  // into the bundle. Real bug found: dev's Cloudflare Pages build never had
  // this variable configured at all, so isAdmin was silently false for
  // everyone in dev regardless of the real backend role. isAdmin now comes
  // from App.jsx, which gets it from the actual GET /api/admin/my-role
  // call — one source of truth, not two independently-configured copies.

  const navigate = (pg) => {
    setMobileOpen(false);
    setUserMenuOpen(false);
    if (pg === 'home') {
      setAnalysis?.(null);
      if (window.location.pathname !== '/') window.history.pushState({}, '', '/');
    }
    setPage(pg);
  };
  const activePage = page === 'stock' ? 'home' : page;

  // Dashboard link only for logged-in members
  const navLinks = [
    { id: 'home',       label: 'Home'       },
    { id: 'market',     label: 'Market'     },
    { id: 'screener',   label: 'Screener'   },
    { id: 'congress',   label: 'Congress'   }, // Congressional Trade Tracker — design doc Section 2 nav placement
    { id: 'calendar',   label: 'Calendar'   },
    { id: 'news',       label: 'News'       },
    { id: 'filings',    label: 'Filings'    }, // SEC-FILINGS-PAGE
    { id: 'financials', label: 'Financials' }, // SCREENER-FINANCIALS
    { id: 'calculator', label: 'Calculator' },
  ];
  // Dashboard added before Pricing for members
  const memberLinks = user ? [
    { id: 'home',       label: 'Home'       },
    { id: 'dashboard',  label: 'Dashboard'  },
    { id: 'market',     label: 'Market'     },
    { id: 'screener',   label: 'Screener'   },
    { id: 'congress',   label: 'Congress'   },
    { id: 'calendar',   label: 'Calendar'   },
    { id: 'news',       label: 'News'       },
    { id: 'filings',    label: 'Filings'    }, // SEC-FILINGS-PAGE
    { id: 'financials', label: 'Financials' }, // SCREENER-FINANCIALS
    { id: 'calculator', label: 'Calculator' },
  ] : navLinks;

  const tierConfig = {
    free:    { label: 'Free',    color: '#64748b', icon: <BarChart2 size={10} /> },
    basic:   { label: 'Basic',   color: '#00d4ff', icon: <Zap size={10} />      },
    premium: { label: 'Premium', color: '#00ff88', icon: <Crown size={10} />    },
  };
  const tier = tierConfig[user?.subscriptionTier] || tierConfig.free;
  const displayName = user?.displayName || user?.email?.split('@')[0] || 'Account';

  return (
    <>
      <header style={{ position: 'sticky', top: 0, zIndex: 100, background: 'rgba(5,13,26,0.95)', backdropFilter: 'blur(20px)', borderBottom: '1px solid rgba(0,212,255,0.08)', WebkitBackdropFilter: 'blur(20px)' }}>
        <div style={{ ...s.container }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '60px', gap: '1rem' }}>

            {/* Logo */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', cursor: 'pointer', flexShrink: 0 }} onClick={() => navigate('home')}>
              <MarketPulzeLogo size={34} />
              <div>
                <div style={{ fontSize: '1.2rem', fontWeight: 900, letterSpacing: '-0.03em', background: 'linear-gradient(135deg,#00ff88,#00d4ff)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', lineHeight: 1 }}>
                  MarketPulze
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginTop: '1px' }}>
                <div style={{ fontSize: '0.58rem', color: '#334155', letterSpacing: '0.1em' }}>AI STOCK INTELLIGENCE</div>
                <div style={{ fontSize: '0.55rem', fontWeight: 900, color: '#ffaa00', background: 'rgba(255,170,0,0.12)', border: '1px solid rgba(255,170,0,0.3)', borderRadius: '4px', padding: '0 0.3rem', letterSpacing: '0.05em' }}>BETA</div>
              </div>
              </div>
            </div>

            {/* Desktop Nav */}
            <nav style={{ display: 'flex', gap: '0.15rem', flex: 1, justifyContent: 'center' }} className="desktop-nav">
              {[...memberLinks, ...(user ? [{ id: 'pricing', label: 'Pricing' }] : [])].map(link => (
                <button key={link.id} onClick={() => navigate(link.id)}
                  style={{ padding: '0.45rem 1rem', borderRadius: '8px', border: 'none', background: activePage === link.id ? 'rgba(0,212,255,0.1)' : 'transparent', color: activePage === link.id ? '#00d4ff' : '#64748b', fontWeight: activePage === link.id ? 700 : 500, cursor: 'pointer', fontSize: '0.875rem', transition: 'all 0.15s', fontFamily: 'inherit' }}>
                  {link.label}
                </button>
              ))}
            </nav>

            {/* Desktop Auth / User */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexShrink: 0 }} className="desktop-auth">
              {user ? (
                <div ref={userMenuRef} style={{ position: 'relative' }}>
                  <button onClick={() => setUserMenuOpen(o => !o)}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.4rem 0.75rem', background: 'rgba(0,212,255,0.06)', border: '1px solid rgba(0,212,255,0.15)', borderRadius: '10px', color: '#f8fafc', cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s' }}>
                    {/* Avatar */}
                    <div style={{ width: 26, height: 26, borderRadius: '50%', background: 'linear-gradient(135deg,#00ff88,#00d4ff)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.65rem', fontWeight: 900, color: '#050d1a', flexShrink: 0 }}>
                      {displayName.slice(0, 2).toUpperCase()}
                    </div>
                    <span style={{ fontSize: '0.85rem', fontWeight: 600, maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayName}</span>
                    {/* Tier badge */}
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.2rem', padding: '0.15rem 0.45rem', background: `${tier.color}18`, border: `1px solid ${tier.color}30`, borderRadius: '20px', fontSize: '0.6rem', fontWeight: 800, color: tier.color }}>
                      {tier.icon}{tier.label}
                    </span>
                    <ChevronDown size={13} style={{ color: '#475569', transition: 'transform 0.2s', transform: userMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)' }} />
                  </button>

                  {/* Dropdown */}
                  {userMenuOpen && (
                    <div style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, background: 'rgba(8,18,40,0.98)', border: '1px solid rgba(0,212,255,0.12)', borderRadius: '14px', padding: '0.5rem', minWidth: '200px', boxShadow: '0 20px 60px rgba(0,0,0,0.5)', backdropFilter: 'blur(20px)', zIndex: 200 }}>
                      {/* User info */}
                      <div style={{ padding: '0.75rem 1rem 0.75rem', borderBottom: '1px solid rgba(255,255,255,0.05)', marginBottom: '0.4rem' }}>
                        <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#f8fafc' }}>{displayName}</div>
                        <div style={{ fontSize: '0.72rem', color: '#475569', marginTop: '0.1rem' }}>{user.email}</div>
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', marginTop: '0.4rem', padding: '0.15rem 0.6rem', background: `${tier.color}15`, border: `1px solid ${tier.color}25`, borderRadius: '20px', fontSize: '0.65rem', fontWeight: 800, color: tier.color }}>
                          {tier.icon} {tier.label} Plan
                        </div>
                      </div>
                      {[
                        { icon: <User size={14} />, label: 'My Profile', action: () => navigate('profile') },
                        ...((isAdmin || isSuperAdmin) ? [{ icon: <Shield size={14} />, label: 'Admin Panel', action: () => navigate('admin') }] : []),
                        // PHASE 4.3 nav-visibility fix (this session): gated on
                        // hasReviewConsoleAccess itself (from GET /my-role,
                        // admin.js) — NOT isAdmin (would wrongly hide this from
                        // a non-admin support account that has the flag) and
                        // NOT unconditional (would wrongly show it to every
                        // member, including ones with zero access at all).
                        // Super Admin already gets hasReviewConsoleAccess=true
                        // from the backend bypass, so no separate isSuperAdmin
                        // check needed here.
                        ...(hasReviewConsoleAccess ? [{ icon: <ClipboardList size={14} />, label: 'Review Console Status', action: () => navigate('reviewStatus') }] : []),
                        ...(isSuperAdmin ? [{ icon: <Zap size={14} />, label: 'Roadmap', action: () => navigate('roadmap') }] : []),
                        { icon: <BarChart2 size={14} />, label: 'Analysis History', action: () => { navigate('profile'); } },
                        { icon: <Settings size={14} />, label: 'Account Settings', action: () => navigate('profile') },
                      ].map((item, i) => (
                        <button key={i} onClick={item.action}
                          style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '0.65rem', padding: '0.65rem 1rem', borderRadius: '8px', border: 'none', background: 'transparent', color: '#94a3b8', cursor: 'pointer', fontSize: '0.83rem', fontFamily: 'inherit', textAlign: 'left', transition: 'all 0.1s' }}
                          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,212,255,0.06)'; e.currentTarget.style.color = '#f8fafc'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#94a3b8'; }}>
                          {item.icon}{item.label}
                        </button>
                      ))}
                      {user.subscriptionTier !== 'premium' && (
                        <button onClick={() => navigate('pricing')}
                          style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '0.65rem', padding: '0.65rem 1rem', borderRadius: '8px', border: 'none', background: 'rgba(0,255,136,0.06)', color: '#00ff88', cursor: 'pointer', fontSize: '0.83rem', fontFamily: 'inherit', textAlign: 'left', fontWeight: 700, marginTop: '0.25rem' }}>
                          <Crown size={14} /> Upgrade Plan
                        </button>
                      )}
                      <div style={{ borderTop: '1px solid rgba(255,255,255,0.05)', marginTop: '0.4rem', paddingTop: '0.4rem' }}>
                        <button onClick={() => { setPage('home'); window.dispatchEvent(new CustomEvent('logout')); setUserMenuOpen(false); }}
                          style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '0.65rem', padding: '0.65rem 1rem', borderRadius: '8px', border: 'none', background: 'transparent', color: '#64748b', cursor: 'pointer', fontSize: '0.83rem', fontFamily: 'inherit', textAlign: 'left' }}
                          onMouseEnter={e => { e.currentTarget.style.color = '#ff4444'; e.currentTarget.style.background = 'rgba(255,68,68,0.06)'; }}
                          onMouseLeave={e => { e.currentTarget.style.color = '#64748b'; e.currentTarget.style.background = 'transparent'; }}>
                          <LogOut size={14} /> Sign Out
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button onClick={() => setShowInvite(true)}
                    style={{ padding: '0.5rem 1rem', background: 'transparent', border: '1px solid rgba(0,255,136,0.3)', borderRadius: '8px', color: '#00ff88', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem', fontFamily: 'inherit', transition: 'all 0.15s' }}>
                    Request Invite
                  </button>
                  <button onClick={() => { setAuthMode('login'); setShowAuth(true); }}
                    style={{ padding: '0.5rem 1rem', background: 'linear-gradient(135deg,#00ff88,#00d4ff)', border: 'none', borderRadius: '8px', color: '#050d1a', cursor: 'pointer', fontWeight: 800, fontSize: '0.85rem', fontFamily: 'inherit' }}>
                    Sign In
                  </button>
                </div>
              )}
            </div>

            {/* Mobile Hamburger */}
            <button onClick={() => setMobileOpen(o => !o)}
              style={{ display: 'none', padding: '0.5rem', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '8px', color: '#94a3b8', cursor: 'pointer' }}
              className="mobile-menu-btn">
              {mobileOpen ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div style={{ background: 'rgba(5,13,26,0.98)', borderTop: '1px solid rgba(0,212,255,0.08)', padding: '1rem' }}>
            {[...memberLinks, ...(user ? [{ id: 'pricing', label: 'Pricing' }] : [])].map(link => (
              <button key={link.id} onClick={() => navigate(link.id)}
                style={{ width: '100%', padding: '0.85rem 1rem', borderRadius: '10px', border: 'none', background: activePage === link.id ? 'rgba(0,212,255,0.1)' : 'transparent', color: activePage === link.id ? '#00d4ff' : '#94a3b8', fontWeight: 600, cursor: 'pointer', fontSize: '0.9rem', textAlign: 'left', fontFamily: 'inherit', marginBottom: '0.25rem', display: 'block' }}>
                {link.label}
              </button>
            ))}
            {user ? (
              <>
                <button onClick={() => navigate('profile')} style={{ width: '100%', padding: '0.85rem 1rem', borderRadius: '10px', border: 'none', background: 'transparent', color: '#94a3b8', fontWeight: 600, cursor: 'pointer', fontSize: '0.9rem', textAlign: 'left', fontFamily: 'inherit', marginBottom: '0.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <User size={14} /> Profile
                </button>
                <button onClick={() => { navigate('home'); window.dispatchEvent(new CustomEvent('logout')); }}
                  style={{ width: '100%', padding: '0.85rem 1rem', borderRadius: '10px', border: 'none', background: 'rgba(255,68,68,0.06)', color: '#ff6b6b', fontWeight: 600, cursor: 'pointer', fontSize: '0.9rem', textAlign: 'left', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <LogOut size={14} /> Sign Out
                </button>
              </>
            ) : (
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                <button onClick={() => { setShowInvite(true); setMobileOpen(false); }} style={{ flex: 1, padding: '0.75rem', background: 'transparent', border: '1px solid rgba(0,255,136,0.3)', borderRadius: '8px', color: '#00ff88', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem', fontFamily: 'inherit' }}>Request Invite</button>
                <button onClick={() => { setAuthMode('login'); setShowAuth(true); setMobileOpen(false); }} style={{ flex: 1, padding: '0.75rem', background: 'linear-gradient(135deg,#00ff88,#00d4ff)', border: 'none', borderRadius: '8px', color: '#050d1a', cursor: 'pointer', fontWeight: 800, fontSize: '0.875rem', fontFamily: 'inherit' }}>Sign In</button>
              </div>
            )}
          </div>
        )}
      </header>

      <style>{`
        @media (max-width: 768px) {
          .desktop-nav { display: none !important; }
          .desktop-auth { display: none !important; }
          .mobile-menu-btn { display: flex !important; }
        }
      `}</style>
    </>
  );
};

export default Header;

